import palette from "./palette";

/**
 * Tokens de cor para as folhas `makeStyles` (@material-ui/core v4).
 *
 * Cada valor carrega ` !important` porque as classes JSS v4 e as classes
 * emotion do MUI v5 têm a mesma especificidade — sem o sufixo a ordem de
 * injeção decide o vencedor, o que é imprevisível neste app híbrido.
 *
 * NÃO defina hex aqui: os valores vêm de `./palette.ts`, que é a fonte de
 * verdade do design system (derivado do mockup Lovable). Para código novo,
 * ou em `sx`, importe `palette` direto — sem `!important`.
 *
 * As chaves antigas são mantidas para não quebrar os ~90 arquivos que já as
 * consomem; várias delas hoje apontam para o mesmo token do design system.
 */
const bang = (hex: string) => `${hex} !important`;

// ------------------------------------------------------------------ neutros
const black = bang("#000000");
const blackMedium = bang(palette.text);
const blackLight = bang(palette.text);
const blackDark = bang(palette.videoShell);
const white = bang(palette.surface);
const whiteLight = bang(palette.background);

const gray = bang(palette.textDisabled);
const grayDLight = bang(palette.border);
const grayMediumLight = bang(palette.border);
const grayLight = bang(palette.page);
const grayLightest = bang(palette.background);
const grayMedium = bang(palette.textMuted);
const grayTitlePoints = bang(palette.textMuted);
const grayClose = bang(palette.textMuted);
const grayFeedbackBg = bang(palette.muted);
const grayBarPerformance = bang(palette.muted);
const grayStatusDataIA = bang(palette.muted);
const grayButtonCam2 = bang(palette.videoBar);

// -------------------------------------------------------------------- marca
/** Sem `!important`: alimenta `createTheme`, que precisa parsear a cor. */
const purplePrimary = palette.brand;
const purple = bang(palette.brand);
const purpleIcon = bang(palette.brand);
const purpleMedium = bang(palette.brand);
const purpleDivDark = bang(palette.brand);
const purpleDetail = bang(palette.brand);
const purpleSubTitle = bang(palette.brand);
const purpleAvatarName = bang(palette.brand);
const purpleTextButton = bang(palette.brand);
const purpleNewDivider = bang(palette.brand);

const purpleSecondary = bang(palette.brandDark);
const purpleDarkest = bang(palette.brandDark);
const purpleMediumSub = bang(palette.brandDark);
const purpleStatus = bang(palette.brandDark);
const blueDark = bang(palette.brandDark);

const lilacLight = bang(palette.brandSoft);
const lilacMedium = bang(palette.brandSoft);
const lilacPerformance = bang(palette.brandSoft);
const purpleLightMark = bang(palette.brandSoft);
const purpleBackButton = bang(palette.brandSoft);
const purpleLightStation = bang(palette.brandWash);

const lilac = bang(palette.footerBand);
const lilacLightest = bang(palette.textDisabled);
const lilacMediumLight = bang(palette.lilacMid);
const lilacDarkMedium = bang(palette.studentBannerTo);
const lilacPerformanceTitle = bang(palette.studentBannerFrom);
const lilacButtonCam1 = bang(palette.lilacMid);
const purpleLightest = bang(palette.textDisabled);

// ------------------------------------------------------------------ sidebar
const purpleDark = bang(palette.sidebar);
const purpleButtonCam = bang(palette.sidebar);
const purpleDarkMedium = bang(palette.sidebarAccent);
const bgSubmenuHover = bang(palette.sidebarAccent);
const purpleMenuActive = bang(palette.sidebarAccent);
const blueDarkest = bang(palette.sidebarAccent);
/** Setas do header e realces frios sobre fundo claro. */
const purpleLight = bang(palette.textMuted);

// -------------------------------------------------------------------- verde
const green = bang(palette.emeraldStrong);
const greenMedium = bang(palette.emeraldStrong);
const greenDark = bang(palette.emeraldDeep);
const greenMediumLight = bang(palette.emeraldDeep);
const greenDarkButton = bang(palette.emeraldDeep);
const greenLight = bang(palette.emeraldSoft);

// ----------------------------------------------------------- alerta / info
const pink = bang(palette.dangerText);
const pinkDark = bang(palette.dangerText);
const pinkLight = bang(palette.danger);
const pinkMedium = bang(palette.danger);
const redLight = bang(palette.dangerSoft);
const redMedium = bang(palette.dangerBorder);
/** Sem `!important`: é passado ao canvas do `react-canvas-draw`. */
const redBrush = "#FF0000";

const blue = bang(palette.info);
const blueMedium = bang(palette.info);
const yellow = bang(palette.warning);

const colors = {
  black,
  redLight,
  redMedium,
  blackLight,
  blackMedium,
  blue,
  blueMedium,
  blueDark,
  blueDarkest,
  gray,
  grayLight,
  grayLightest,
  green,
  greenDark,
  greenMedium,
  lilac,
  lilacDarkMedium,
  lilacLight,
  pinkMedium,
  lilacLightest,
  lilacMedium,
  lilacMediumLight,
  lilacPerformance,
  lilacPerformanceTitle,
  grayDLight,
  grayMedium,
  grayBarPerformance,
  pink,
  pinkDark,
  pinkLight,
  purple,
  purpleDark,
  purpleDarkest,
  purpleDarkMedium,
  purpleDivDark,
  purpleIcon,
  purpleLight,
  purpleLightest,
  purpleMedium,
  bgSubmenuHover,
  purpleMenuActive,
  purpleMediumSub,
  purplePrimary,
  purpleSecondary,
  purpleTextButton,
  purpleBackButton,
  purpleSubTitle,
  purpleAvatarName,
  purpleLightStation,
  purpleStatus,
  purpleDetail,
  purpleLightMark,
  white,
  yellow,
  grayTitlePoints,
  blackDark,
  greenMediumLight,
  grayFeedbackBg,
  redBrush,
  grayMediumLight,
  purpleButtonCam,
  lilacButtonCam1,
  grayButtonCam2,
  grayStatusDataIA,
  greenDarkButton,
  greenLight,
  whiteLight,
  grayClose,
  purpleNewDivider,
};

export default colors;
